import { FC, RefObject, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import {
	AlertDialog,
	AlertDialogBody,
	AlertDialogCloseButton,
	AlertDialogContent,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogOverlay,
	Badge,
	Box,
	Button,
	HStack,
	Spinner,
	Text,
	useDisclosure,
	useToast,
	VStack,
} from '@bonusly/design-system';

import { useBizyEverboarding, useBizyResetEverboardingMutation } from '../../queries/bizy-everboarding';
import { BizyEverboardingStatus } from '../../schemas/bizy-everboarding';

// ─── Status display helpers ──────────────────────────────────────────────────

const STATUS_COLOR_SCHEMES: Record<BizyEverboardingStatus, string> = {
	not_started: 'gray',
	in_progress: 'blue',
	completed: 'green',
	dismissed: 'orange',
};

function formatTimestamp(date: Date | null): string {
	if (!date) return '—';
	return date.toLocaleDateString(undefined, {
		year: 'numeric',
		month: 'short',
		day: 'numeric',
		hour: 'numeric',
		minute: '2-digit',
	});
}

// ─── Timestamp Row ───────────────────────────────────────────────────────────

const TimestampRow: FC<{ label: string; date: Date | null }> = ({ label, date }) => (
	<HStack justify="space-between" spacing={2} align="baseline">
		<Text size="sm" color="text.soft">
			{label}
		</Text>
		<Text size="sm" color="text.default" fontFamily="mono">
			{formatTimestamp(date)}
		</Text>
	</HStack>
);

// ─── Everboarding Section ────────────────────────────────────────────────────

export const EverboardingSection = () => {
	const { t } = useTranslation('bizy_settings');
	const { data: everboarding, isLoading } = useBizyEverboarding();
	const resetMutation = useBizyResetEverboardingMutation();
	const toast = useToast();

	const confirmDialog = useDisclosure();
	const cancelRef = useRef<HTMLButtonElement>(null);

	const handleReset = () => {
		resetMutation.mutate(undefined, {
			onSuccess: () => {
				confirmDialog.onClose();
				toast({ title: t('everboarding.resetSuccess'), status: 'success', duration: 3000 });
			},
			onError: () => {
				confirmDialog.onClose();
				toast({ title: t('everboarding.resetError'), status: 'error', duration: 3000 });
			},
		});
	};

	const canReset = everboarding && everboarding.status !== 'not_started';

	return (
		<Box>
			<Box pb={3} borderBottom="1px solid" borderColor="border.soft">
				<Text size="md" color="text.softest">
					{t('sectionIntro.notifications')}
				</Text>
			</Box>

			{isLoading ? (
				<HStack justify="center" py={4}>
					<Spinner size="sm" />
				</HStack>
			) : everboarding ? (
				<VStack spacing={0} align="stretch">
					<Box borderBottom="1px solid" borderColor="border.soft" py={3}>
						<VStack spacing={3} align="stretch">
							<HStack justify="space-between" align="center" spacing={3}>
								<Text size="sm" variant="medium" color="text.soft">
									{t('everboarding.statusLabel')}
								</Text>
								{everboarding && (
									<Badge colorScheme={STATUS_COLOR_SCHEMES[everboarding.status]} fontSize="xs">
										{t(`everboarding.statuses.${everboarding.status}`)}
									</Badge>
								)}
							</HStack>

							<VStack spacing={2} align="stretch">
								<TimestampRow label={t('everboarding.startedAt')} date={everboarding.createdAt} />
								<TimestampRow label={t('everboarding.completedAt')} date={everboarding.completedAt} />
								<TimestampRow label={t('everboarding.dismissedAt')} date={everboarding.dismissedAt} />
							</VStack>
						</VStack>
					</Box>

					<Box pt={3}>
						<Button size="md" variant="outline" colorScheme="secondary" isDisabled={!canReset} onClick={confirmDialog.onOpen}>
							{t('everboarding.reset')}
						</Button>
					</Box>
				</VStack>
			) : (
				<Text size="sm" color="text.softer" pt={3}>
					{t('everboarding.loadError')}
				</Text>
			)}

			<AlertDialog leastDestructiveRef={cancelRef as RefObject<HTMLButtonElement>} isOpen={confirmDialog.isOpen} onClose={confirmDialog.onClose} isCentered>
				<AlertDialogOverlay>
					<AlertDialogContent>
						<AlertDialogHeader fontSize="lg" fontWeight="bold">
							{t('everboarding.confirmTitle')}
						</AlertDialogHeader>
						<AlertDialogCloseButton />
						<AlertDialogBody>
							<Text size="md">{t('everboarding.confirmBody')}</Text>
						</AlertDialogBody>
						<AlertDialogFooter gap={3}>
							<Button ref={cancelRef} variant="outline" colorScheme="secondary" size="md" onClick={confirmDialog.onClose}>
								{t('cancel')}
							</Button>
							<Button variant="solid" colorScheme="danger" size="md" onClick={handleReset} isLoading={resetMutation.isLoading}>
								{t('everboarding.reset')}
							</Button>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialogOverlay>
			</AlertDialog>
		</Box>
	);
};
